/**
 * HTTP service for the Stickyboard surface.
 *
 * Mirrors the VAU service pattern (`features/vau/services/vau.ts`): each
 * function returns an async fetcher so the call site hands it straight to
 * TanStack Query, and `.key()` produces the matching query-key tuple for
 * targeted invalidation.
 *
 * Three backends, all through the gateway (which injects X-Opal-Instance-ID +
 * X-Opal-User-ID from the OptiID token):
 *   - messaging-service  /messaging-service/api/v1/{stickies,sections,stacks}
 *   - hypatia            /hypatia/api/v1/{instance}/stickyboard/curate
 */

import http from '@frontend/api/http';
import { HYPATIA_URL, OPAL_URL } from '@frontend/envConfig';
import type { Section, Stack, StickyCard } from '@frontend/types/stickyboard';

const MSG_BASE = `${OPAL_URL}/messaging-service/api/v1`;

// ---------- stickies ----------

export type StickyFilter = {
  section_id?: string;
  status?: 'active' | 'dismissed' | 'archived';
  sender_id?: string;
  stack_id?: string;
  only_vau?: boolean;
  pinned?: boolean;
  limit?: number;
  cursor?: string;
};

export type StickyListResponse = {
  stickies: StickyCard[];
  has_more: boolean;
};

export type StickyUpdatePayload = {
  user_section_id_override?: string;
  user_pinned?: boolean;
  user_stack_override?: string;
  user_card_data?: Record<string, unknown>;
  status?: 'active';
};

export function listStickies(filter: StickyFilter = {}) {
  return async () => {
    const res = await http.get<StickyListResponse>(`${MSG_BASE}/stickies`, { params: filter });
    return res.data;
  };
}
listStickies.key = (filter: StickyFilter = {}) => ['stickyboard', 'stickies', filter] as const;

export function searchStickies(query: string) {
  return async () => {
    const res = await http.get<StickyListResponse>(`${MSG_BASE}/stickies/search`, {
      params: { q: query },
    });
    return res.data;
  };
}
searchStickies.key = (query: string) => ['stickyboard', 'stickies', 'search', query] as const;

export async function patchSticky(
  stickyId: string,
  payload: StickyUpdatePayload
): Promise<StickyCard> {
  const res = await http.patch<StickyCard>(
    `${MSG_BASE}/stickies/${encodeURIComponent(stickyId)}`,
    payload
  );
  return res.data;
}

export async function dismissSticky(
  stickyId: string
): Promise<{ sticky: StickyCard | null; skipped?: boolean }> {
  const res = await http.post<{ sticky: StickyCard | null; skipped?: boolean }>(
    `${MSG_BASE}/stickies/${encodeURIComponent(stickyId)}/dismiss`
  );
  return res.data;
}

// ---------- sections ----------

export type SectionListResponse = { sections: Section[] };
export type CreateSectionPayload = { label: string; position?: number };
export type SectionPatch = { label?: string; position?: number };

export function listSections() {
  return async () => {
    const res = await http.get<SectionListResponse>(`${MSG_BASE}/sections`);
    return res.data.sections ?? [];
  };
}
listSections.key = () => ['stickyboard', 'sections'] as const;

export async function createSection(payload: CreateSectionPayload): Promise<Section> {
  const res = await http.post<Section>(`${MSG_BASE}/sections`, payload);
  return res.data;
}

export async function updateSection(sectionId: string, patch: SectionPatch): Promise<Section> {
  const res = await http.patch<Section>(
    `${MSG_BASE}/sections/${encodeURIComponent(sectionId)}`,
    patch
  );
  return res.data;
}

export async function deleteSection(sectionId: string): Promise<void> {
  await http.delete(`${MSG_BASE}/sections/${encodeURIComponent(sectionId)}`);
}

// ---------- stacks ----------

export type StackListResponse = { stacks: Stack[] };
export type CreateStackPayload = { label: string };
export type StackPatch = { label?: string };

export function listStacks() {
  return async () => {
    const res = await http.get<StackListResponse>(`${MSG_BASE}/stacks`);
    return res.data.stacks ?? [];
  };
}
listStacks.key = () => ['stickyboard', 'stacks'] as const;

export async function createStack(payload: CreateStackPayload): Promise<Stack> {
  const res = await http.post<Stack>(`${MSG_BASE}/stacks`, payload);
  return res.data;
}

export async function updateStack(stackId: string, patch: StackPatch): Promise<Stack> {
  const res = await http.patch<Stack>(`${MSG_BASE}/stacks/${encodeURIComponent(stackId)}`, patch);
  return res.data;
}

export async function deleteStack(stackId: string): Promise<void> {
  await http.delete(`${MSG_BASE}/stacks/${encodeURIComponent(stackId)}`);
}

// ---------- curate (hypatia, fire-and-forget) ----------

export type CurateResponse = { execution_id: string; status: string };

export async function curateBoard(instanceId: string): Promise<CurateResponse> {
  const res = await http.post<CurateResponse>(
    `${HYPATIA_URL}/api/v1/${encodeURIComponent(instanceId)}/stickyboard/curate`
  );
  return res.data;
}
